import { Component, Element, State, h, Event, EventEmitter, Prop, getAssetPath } from '@stencil/core'
import { coveoQuerySuggestApiUrl, coveoValuesApiUrl, retailSearchResultsPage } from 'src/global/resources'
import { QuerySuggestModel, QuerySuggestDataGroup } from 'src/model/query-suggest.model'
import { ValuesModel, ValuesDataGroup } from 'src/model/values.model'
import { fetchData } from 'src/utils/http-requests.util'
import { searchResultClick } from 'src/model/search-result-clicked-object.model'

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions, @typescript-eslint/no-explicit-any
declare global { interface Window { dataLayer?: any[] } }

@Component({
  tag: 'search-widget',
  styleUrl: 'search-widget.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class SearchWidget {
  @State() searchQuery = ''
  @State() onDropdown = false
  @State() products: ValuesDataGroup[] = []
  @State() suggestion: QuerySuggestDataGroup[] = []
  @State() recentSearches: string[] = []
  @Prop() searchResultClickObj: searchResultClick

  @Element() el: HTMLElement

  @Event({ bubbles: true }) previousNavigationEvent: EventEmitter<string>

  @Event({ bubbles: true }) searchResultClicked: EventEmitter<searchResultClick>

  private searchInput!: HTMLInputElement

  private searchResultClickedHandler(submission: searchResultClick) {
    this.searchResultClicked.emit(submission)
  }

  // Lifecycle Methods
  componentWillLoad() {
    this.initializeSearchResultClickObj()
    this.addKeyboardEventListeners()
    this.onDropdown = false
    this.searchInput?.focus()
  }

  componentDidLoad() {
    this.toggleBodyStyle(window.innerWidth <= 990)
  }

  componentWillRender() {
    if (this.searchQuery == '') {
      this.clearSuggestionsAndProducts()
    }
  }

  disconnectedCallback() {
    this.toggleBodyStyle(false) // Ensure scrolling is enabled when the component is removed
    this.removeEventListeners()
  }

  // Helper Methods
  private backArrowHandler(Submission: string) {
    this.toggleBodyStyle(false)
    this.previousNavigationEvent.emit(Submission)
  }

  private updateDropdownStatus(value: boolean) {
    this.onDropdown = value
  }

  private getRecentSearches() {
    // retrieves recentSearches from local storage to be displayed
    const searchSession = window.localStorage.recentSearches
    if (searchSession) {
      // recentSearches in the local storage are in this format: recentSearches=aapl},{goog
      this.updateDropdownStatus(true)
      // recent searches seperator is },{
      this.recentSearches = searchSession.split('},{')
    }
  }

  private updateRecentSearches(expression: string) {
    // adds search term to recent search array
    // if user input does not contain atleast one letter or number, do not add it to recent searches
    if (expression.match('.*[a-zA-Z0-9].*') !== null) {
      // ignore all leading equal signs
      expression = expression.replace(/^=+/, '')
      const checkedExpression = expression.replaceAll('},{', '').trim().replace(/<[^>]*>?/gm, '')
      if (checkedExpression !== '' && !this.recentSearches.includes(checkedExpression)) {
        this.recentSearches.unshift(checkedExpression)
      }
    }
  }

  private handleUpdateRecentSearches(expression = '') {
    // Add recent searches to user's local storage
    const searchTerm = expression || this.searchQuery
    this.updateRecentSearches(searchTerm)
    // Limit recent searches to 5 and update local storage
    if (this.recentSearches.length > 0) {
      this.recentSearches = this.recentSearches.slice(0, 5)
      window.localStorage.recentSearches = this.recentSearches.join('},{')
    }
  }

  private handleBackButton() {
    this.backArrowHandler('backArrow')
    this.updateDropdownStatus(false)
  }

  private clearInput() {
    this.searchQuery = ''
    this.searchInput.focus()
  }

  private clearSuggestionsAndProducts() {
    this.suggestion = []
    this.products = []
  }

  async handleSearchInputChange(event?: InputEvent) {
    this.searchQuery = (event.target as HTMLInputElement).value
    if (this.searchQuery.length < 1) {
      this.clearSuggestionsAndProducts()
      this.clearInput()
      this.getRecentSearches()
    } else {
      this.updateDropdownStatus(true)
      try {
        const [querySuggestRes, valuesRes] = await Promise.all([
          fetchData<QuerySuggestModel>(coveoQuerySuggestApiUrl, this.searchQuery),
          fetchData<ValuesModel>(coveoValuesApiUrl, this.searchQuery),
        ])
        this.suggestion = querySuggestRes.completions
        this.products = valuesRes.values
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
  }

  private onSearchQuery() {
    this.handleViewAllResults()
  }

  private handleModalBg() {
    this.handleBackButton()
    this.updateDropdownStatus(false)
  }

  private handleViewAllResults() {
    if (this.searchQuery !== '') {
      this.toggleBodyStyle(false)
      this.handleUpdateRecentSearches()
      window.location.assign(`${retailSearchResultsPage}${this.searchQuery}`)
      setTimeout(() => {
        console.log('Inside the handleViewAllResults')
      }, 1000)
    }
  }

  private handleFocus() {
    // getting the recent searches on focus
    this.getRecentSearches()
    if (this.recentSearches.length > 0) this.onDropdown = true
    this.toggleBodyStyle(window.innerWidth <= 990)
  }

  private toggleBodyStyle(disable: boolean) {
    document.body.style.overflow = disable ? 'hidden' : ''
  }

  private addKeyboardEventListeners() {
    document.addEventListener('focusout', this.handleFocusOut)
    document.addEventListener('keydown', this.handleKeyDown)
  }

  private handleFocusOut = (event: FocusEvent) => {
    const target = event.target as HTMLElement
    if (target.shadowRoot.querySelectorAll('search-widget')) {
      this.onDropdown = false
    }
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    const searchWidgetDropdown = this.el.shadowRoot.querySelector('search-dropdown')
    const dropdownItems = searchWidgetDropdown.shadowRoot.querySelectorAll('.search-widget-dropdown-list-item')
    const focusedElement = searchWidgetDropdown.shadowRoot.activeElement
    const currentIndex = Array.from(dropdownItems).indexOf(focusedElement as HTMLElement)

    if (event.key === 'Escape') this.toggleBodyStyle(true)
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (currentIndex >= 0 && currentIndex < dropdownItems.length - 1) {
        (dropdownItems[currentIndex + 1] as HTMLElement).focus()
      } else if (currentIndex === -1 && dropdownItems.length > 0) {
        (dropdownItems[0] as HTMLElement).focus()
      }
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (currentIndex > 0 && currentIndex <= dropdownItems.length) {
        (dropdownItems[currentIndex - 1] as HTMLElement).focus()
      } else if (currentIndex === 0 && dropdownItems.length > 0) {
        this.searchInput.focus()
      }
    }
  }

  private removeEventListeners() {
    document.removeEventListener('focusout', this.handleFocusOut)
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  private initializeSearchResultClickObj() {
    this.searchResultClickObj = {
      event: 'searchResultClicked',
      searchInfo: {
        resultClicked: '', // evar 41
        searchTermEntered: '', // (c27,v27)
        searchType: 'autosuggest click', // autosuggest or global (v91)
      },
    }
  }

  private createSearchClickObject(searchTermClicked: string, searchType: string) {
    console.log('create search click object clicked')
    this.searchResultClickObj.searchInfo.resultClicked = searchTermClicked
    this.searchResultClickObj.searchInfo.searchTermEntered = this.searchQuery
    this.searchResultClickObj.searchInfo.searchType = searchType
    this.searchResultClickedHandler(this.searchResultClickObj)
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(this.searchResultClickObj)
  }

  private changeAriaExpanded(isDropdownOpen: boolean): string {
    return isDropdownOpen ? 'true' : 'false'
  }

  render() {
    return (
      <div id="search-widget">
        <div class="modal-overlay-bg" onClick={() => this.handleModalBg()}></div>
        <div class="search-widget-container">
          <div class="search-input-container">
            <button
              type="button"
              class="back-button"
              onClick={() => this.handleBackButton()}
              aria-label="close search"
            >
              <img src={getAssetPath('./assets/left-arrow.png')} alt="close search" />
            </button>
            <div
              role="search"
              style={{
                '--col1-width': '15%',
                '--col2-width': '45%',
                '--col3-width': '40%',
              }}
              class="c11n-search c11n-search--medium c11n-search--below c11n-search--attached c11n-search--on-light c11n-search--cols3"
            >
              <form
                class="c11n-search__form"
                method="get"
                action={`${retailSearchResultsPage}${this.searchQuery}`}
                onSubmit={() => {
                  this.onSearchQuery()
                  this.handleBackButton()
                }}
              >
                <div
                  class="c11n-search__menu__sr-only"
                  id="text-input-id-input-description"
                >
                  press enter to search
                </div>
                <div class="c11n-search__form__input-row">
                  <div class="c11n-search__form__input-row__input">
                    <div class="c11n-input c11n-input--medium">
                      <input
                        type="text"
                        id="searchInput"
                        ref={el => this.searchInput = el!}
                        aria-describedby="text-input-id-input-description"
                        value={this.searchQuery}
                        class="c11n-input__input"
                        onInput={e => this.handleSearchInputChange(e)}
                        placeholder="Search or get a quote"
                        aria-controls="searchWidgetDropdown"
                        aria-haspopup="menu"
                        aria-expanded={this.changeAriaExpanded(this.onDropdown)}
                        role="combobox"
                        onFocus={() => this.handleFocus()}
                      />
                      <div class="c11n-input__focus-element"></div>
                    </div>
                    <clear-search-button
                      show={this.searchQuery !== ''}
                      onClick={() => this.clearInput()}
                    />
                  </div>
                  <search-button
                    onClick={() => {
                      this.handleViewAllResults()
                      this.handleBackButton()
                      this.createSearchClickObject('Search Magnifying Glass', 'autosuggest click - Magnifying Glass')
                    }}
                    aria-controls="searchWidgetDropdown"
                    aria-haspopup="menu"
                    aria-expanded={this.changeAriaExpanded(this.onDropdown)}
                  />
                </div>
              </form>
            </div>
          </div>
          {/* dropdown */}
          {this.onDropdown && (
            <search-dropdown
              expanded={this.onDropdown}
              products={this.products}
              suggestion={this.suggestion}
              searchQuery={this.searchQuery}
              handleViewAllResults={this.handleViewAllResults}
              recentSearches={this.recentSearches}
              handleUpdateRecentSearches={this.handleUpdateRecentSearches}
              updateRecentSearches={this.updateRecentSearches}
              previousNavigationEvent={this.previousNavigationEvent}
              createSearchClickObject={this.createSearchClickObject}
              searchResultClickedHandler={this.searchResultClickedHandler}
              searchResultClicked={this.searchResultClicked}
              initializeSearchResultClickObj={this.initializeSearchResultClickObj}
            />
          )}
        </div>
      </div>
    )
  }
}
